import { BaseTenantSearch } from '@/services/_base/BaseTenantSearch.ts'
import { type Person } from '@/types/Person.ts'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class PersonSearch extends BaseTenantSearch<Person> {
  constructor(session: Session, firestore: IFirestoreService) {
    super('people', session, 'person.search', firestore)
  }

  /** Filter to people with a non-empty email */
  withEmail() {
    // Range filter excludes empty strings and null/undefined
    this.where('email', '>=', ' ').where('email', '<=', '\uf8ff')
    this.appendToMetaName('withEmail')
    return this
  }

  /** Prefix search on precomputed lowercased `searchName` */
  prefixFirstName(termRaw: string, limit = 25) {
    const t = termRaw.trim().toLowerCase()
    const end = `${t}\uf8ff`
    this.where('firstName_lower', '>=', t)
      .where('firstName_lower', '<=', end)
      .orderBy('firstName_lower', 'asc')
      .limit(limit)
    this.appendToMetaName('prefix')
    return this
  }

  prefixLastName(termRaw: string, limit = 25) {
    const t = termRaw.trim().toLowerCase()
    const end = `${t}\uf8ff`
    this.where('lastName_lower', '>=', t)
      .where('lastName_lower', '<=', end)
      .orderBy('lastName_lower', 'asc')
      .limit(limit)
    return this
  }

  /** List constrained by organization */
  byOrganization(organizationId: string) {
    this.where('organizationId', '==', organizationId)
      .orderBy('lastName', 'asc')
      .orderBy('firstName', 'asc')
    this.appendToMetaName('byOrg')
    return this
  }

  /** Find by 2D barcode hash (exact match) */
  by2dBarcode(barcodeHash: string) {
    this.where('barcodeHash', '==', barcodeHash)
    this.appendToMetaName('by2dBarcode')
    return this
  }

  /** Find by ID number (exact match) */
  byIdNumber(idNumber: string) {
    this.where('idNumber', '==', idNumber)
    this.appendToMetaName('byIdNumber')
    return this
  }

  /** Find by exact name match using normalized fields */
  byExactName(firstName?: string, lastName?: string) {
    if (firstName) {
      this.where('firstName_lower', '==', firstName.toLowerCase())
    }
    if (lastName) {
      this.where('lastName_lower', '==', lastName.toLowerCase())
    }
    this.appendToMetaName('byExactName')
    return this
  }

  /** Get recently updated people */
  recentlyUpdated(limit = 50) {
    // Ensure updatedAt is the only order so required index matches expectations
    this.spec.orders = []
    this.orderBy('updatedAt', 'desc')
    this.limit(limit)
    this.appendToMetaName('recent')
    return this
  }

  /** Count suppliers (status === 'supplier') */
  countSuppliers() {
    this.where('type', '==', 'supplier')
    this.appendToMetaName('countSuppliers')
    return this
  }

  /** Count prospects (status === 'prospect') */
  countProspects() {
    this.where('status', '==', 'prospect')
    this.appendToMetaName('countProspects')
    return this
  }

  /** Count leads (status === 'lead') */
  countLeads() {
    this.where('status', '==', 'lead')
    this.appendToMetaName('countLeads')
    return this
  }

  /** Count suspects (status === 'suspect') */
  countSuspects() {
    this.where('status', '==', 'suspect')
    this.appendToMetaName('countSuspects')
    return this
  }

  /** Filter by supplier type (type === 'supplier') */
  bySupplierStatus() {
    this.where('type', '==', 'supplier')
    this.appendToMetaName('bySupplierStatus')
    return this
  }

  /** Filter by prospect status (status === 'prospect') */
  byProspectStatus() {
    this.where('status', '==', 'prospect')
    this.appendToMetaName('byProspectStatus')
    return this
  }

  /** Filter by lead status (status === 'lead') */
  byLeadStatus() {
    this.where('status', '==', 'lead')
    this.appendToMetaName('byLeadStatus')
    return this
  }

  /** Filter by suspect status (status === 'suspect') */
  bySuspectStatus() {
    this.where('status', '==', 'suspect')
    this.appendToMetaName('bySuspectStatus')
    return this
  }

  /** Find people by first and last name (partial match) */
  byName(firstName?: string, lastName?: string) {
    if (firstName) {
      this.where('firstName_lower', '>=', firstName.toLowerCase()).where(
        'firstName_lower',
        '<=',
        `${firstName.toLowerCase()}\uf8ff`,
      )
    }
    if (lastName) {
      this.where('lastName_lower', '>=', lastName.toLowerCase()).where(
        'lastName_lower',
        '<=',
        `${lastName.toLowerCase()}\uf8ff`,
      )
    }
    this.appendToMetaName('byName')
    return this
  }

  /** Optional: quick paging helpers if/when you add cursors to the spec */
  startAfter(...values: unknown[]) {
    this.cursor('startAfter', ...values)
    return this
  }

  /** Filter to people with incomplete profiles (empty firstName) */
  incompleteProfiles() {
    this.where('firstName', '==', null)
    this.appendToMetaName('incomplete')
    return this
  }

  /** Filter to people who need to be contacted (lastContactAt older than days ago) */
  toContact(daysAgo: number) {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo)
    this.where('lastContactAt', '<', cutoffDate)
    this.where('isDeleted', '==', false)
    this.appendToMetaName('toContact')
    return this
  }
}
